import { useQuery } from "@tanstack/react-query";
import type { UseQueryOptions } from "@tanstack/react-query";

import { filterSecondaryCarrierOptions } from "@/features/insurance/helpers/filter-secondary-options";
import type { InsuranceListOptions, InsuranceListResponse } from "@/features/insurance/types";
import { mapCarriersToOptions } from "@/features/insurance/helpers/map-to-options";
import { SHORT_LIVED_CACHE } from "@/lib/constants/cache";

import { getInsuranceList } from "../queryFunctions/getInsuranceList";
import { insuranceListKeys } from "../keyFactory";

type UseMappedInsuranceListProps = {
  enabled?: boolean;
  isSecondary?: boolean;
};

type InsuranceMappedQueryOptions = UseQueryOptions<
  InsuranceListResponse,
  unknown,
  InsuranceListResponse,
  ReturnType<typeof insuranceListKeys.all>
>;

export function useMappedInsuranceList({
  enabled = true,
  isSecondary = false,
}: UseMappedInsuranceListProps = {}) {
  return useQuery({
    ...optionFactory(),
    enabled,
    select: (data: InsuranceListResponse): InsuranceListOptions => {
      const mapped = mapCarriersToOptions(data);

      return {
        carrierOptions: isSecondary
          ? filterSecondaryCarrierOptions(mapped.carrierOptions)
          : mapped.carrierOptions,
        stateOptions: mapped.stateOptions,
      };
    },
  });
}

function optionFactory() {
  return {
    queryFn: getInsuranceList,
    queryKey: insuranceListKeys.all(),
    refetchOnWindowFocus: false,
    staleTime: SHORT_LIVED_CACHE,
  } satisfies InsuranceMappedQueryOptions;
}
